import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AppModal,
  AppRemixIcon,
  AppNotificationAlert,
} from '@/layout/AppComponents';
import ReactDOMServer from 'react-dom/server';
import { ProgressBar } from 'react-bootstrap';
import 'react-dropzone-uploader/dist/styles.css';
import { axiosPath, path } from '../../../../routing/helpers/router';
import * as moduleAction from '../../../../store/actions/moduleAction';
import ObligationFormQuestionSelect from '../../../../layout/screenSpecifics/ObligationForm/ObligationFormQuestionSelect';
import HelpModal from '../../../../layout/components/Modal/HelpModal';
import BreadcrumbHandler from '../../../../layout/components/breadcrumb/BreadcrumbHandler';
import Translator from '../../../../layout/components/helpers/Translator';
import KeepSession from '../../../../layout/components/helpers/KeepSession';
import colors from '../../../../constants/colors';
import useIsMounted from '../../../../layout/components/hooks/useIsMounted';
import _ from 'lodash';

const LegalCompliancePlantObligationFormMakeScreen = () => {
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const { moduleBridgeHex, plantHex } = useParams();
  const global = useSelector((state) => state.global);
  const permissions = useSelector(
    (s) => s.module?.[moduleBridgeHex]?.permissions
  );
  const [plantObligationForm, setPlantObligationForm] = useState();
  const [selectedValues, setSelectedValues] = useState({});
  const [moduleBridge, setModuleBridge] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showIsFinishedModal, setShowIsFinishedModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [obligationFormResult, setObligationFormResult] = useState();
  const [continueCheck, setContinueCheck] = useState(0);
  const [modalResultInfo, setModalResultInfo] = useState({
    percent: 1,
    message: ReactDOMServer.renderToString(
      <Translator
        file="module-legalCompliance"
        name="loading-form-information"
      />
    ),
  });
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [modalAssignInfo, setModalAssignInfo] = useState({
    percent: 1,
    message: ReactDOMServer.renderToString(
      <Translator file="module-legalCompliance" name="assigning.obligations" />
    ),
  });
  const [isDownloadingFile, setIsDownloadingFile] = useState(false);
  const [draft, setDraft] = useState();

  useEffect(() => {
    (async () => {
      try {
        const response = await axios.post(
          axiosPath('api_r_user_m_legalCompliance_getPlantObligationFormData'),
          {
            moduleBridgeHex,
            plantHex,
          }
        );
        if (isMounted.current) {
          setModuleBridge(response.data);
          if (!permissions) {
            await dispatch(
              moduleAction.fetch(
                axiosPath(
                  'api_r_user_module_legalCompliance_evaluation_getPermissions'
                ),
                'permissions',
                {
                  moduleBridgeHex,
                  plantHex,
                  subType: 'permissions',
                }
              )
            );
          }
        }
      } catch (err) {}
    })();
  }, [moduleBridgeHex, plantHex, permissions]);

  useEffect(() => {
    (async () => {
      try {
        const response = await axios.post(
          axiosPath('api_r_user_m_legalCompliance_getPlantObligationForm'),
          {
            plantHex,
            languageSlug: global.urlData.lang,
          }
        );
        if (isMounted.current) {
          if (response.data.locations) {
            setPlantObligationForm(response.data);
            if (response.data?.draft) setDraft(response.data.draft);
          }
          if (response.data.notFound) {
            navigate(
              path({
                name: 'user_home',
              })
            );
          }
          setSelectedValues(
            !_.isEmpty(response?.data?.result?.userQuestions)
              ? response.data.result?.userQuestions
              : response?.data?.questionsDefaultValues
          );
        }
      } catch (err) {}
    })();
  }, []);

  const handleQuestionOnChange = (question, answers) => {
    dispatch(KeepSession());
    let updateSelectedValues = {
      ...selectedValues,
    };
    updateSelectedValues[question.code] = answers;
    if (question.type === 4 || question.type === 6) {
      if (question.answers[1].code === answers) {
        if (
          plantObligationForm.subGroupQuestionsDefaultValues &&
          question.code in plantObligationForm.subGroupQuestionsDefaultValues
        ) {
          const subquestions =
            plantObligationForm.subGroupQuestionsDefaultValues[question.code];
          updateSelectedValues = {
            ...updateSelectedValues,
            ...subquestions,
          };
        }
      } else if (question.answers[0].code === answers) {
        if (
          plantObligationForm.subGroupQuestionsDefaultValues &&
          question.code in plantObligationForm.subGroupQuestionsDefaultValues
        ) {
          const removeSubquestions = _.difference(
            _.keys(updateSelectedValues),
            _.keys(plantObligationForm.subGroupQuestionsCodes[question.code])
          );
          updateSelectedValues = _.pickBy(updateSelectedValues, (value, key) =>
            removeSubquestions.includes(key)
          );
        }

        if (question?.subquestions) {
          question?.subquestions.map(
            (sq) => delete updateSelectedValues[sq?.code]
          );
        }
      }
    }
    setSelectedValues(updateSelectedValues);
  };

  const handleOnFinish = useCallback(async () => {
    setIsLoading(true);
    await axios
      .post(
        axiosPath('api_r_user_m_legalCompliance_plantObligationFormFinish'),
        {
          moduleBridgeHex,
          plantHex,
          result: selectedValues,
        }
      )
      .then(() => {
        setIsLoading(false);
        if (isMounted.current) {
          if (permissions?.gcl?.module?.plant?.userObligationForm?.autoAssign) {
            setShowResultsModal(true);
            setContinueCheck(Math.random());
          } else {
            setShowIsFinishedModal(true);
          }
        }
      })
      .catch(() => {
        setIsLoading(false);
        AppNotificationAlert({
          title: ReactDOMServer.renderToString(
            <Translator file="module-legalCompliance" name="error" />
          ),
          message: ReactDOMServer.renderToString(
            <Translator file="module-legalCompliance" name="error" />
          ),
          type: 'danger',
          position: 'top-center',
          duration: 0,
        });
      });
  }, [selectedValues, permissions]);

  const handleSaveDraft = useCallback(async () => {
    setIsLoading(true);
    await axios
      .post(
        axiosPath('api_r_user_m_legalCompliance_plantObligationFormDraft'),
        {
          moduleBridgeHex,
          plantHex,
          result: selectedValues,
        }
      )
      .then((r) => {
        setIsLoading(false);
        setDraft(r.data?.draft);
        if (isMounted.current) {
          const alert = {
            title: ReactDOMServer.renderToString(
              <Translator file="module-legalCompliance" name="draft" />
            ),
            message: ReactDOMServer.renderToString(
              <Translator
                file="module-legalCompliance"
                name="form.saved.draft"
              />
            ),
          };

          AppNotificationAlert({
            title: alert.title,
            message: alert.message,
            type: 'info',
            position: 'top-center',
            duration: 5000,
          });
        }
      })
      .catch(() => {
        setIsLoading(false);
        AppNotificationAlert({
          title: ReactDOMServer.renderToString(
            <Translator file="module-legalCompliance" name="error" />
          ),
          message: ReactDOMServer.renderToString(
            <Translator file="module-legalCompliance" name="error" />
          ),
          type: 'danger',
          position: 'top-center',
          duration: 0,
        });
      });
  }, [selectedValues]);

  const loadObligationFormResults = useCallback(async () => {
    try {
      const response = await axios.post(
        axiosPath('api_r_user_m_legalCompliance_plantObligationFormResults'),
        {
          moduleBridgeHex,
          plantHex,
        }
      );
      return response.data;
    } catch (err) {}
  });

  useEffect(() => {
    if (showResultsModal) {
      loadObligationFormResults()
        .then((response) => {
          if (isMounted.current) setObligationFormResult(response);
        })
        .catch(() => {
          AppNotificationAlert({
            title: ReactDOMServer.renderToString(
              <Translator file="module-legalCompliance" name="error" />
            ),
            message: ReactDOMServer.renderToString(
              <Translator file="module-legalCompliance" name="error" />
            ),
            type: 'danger',
            position: 'top-center',
            duration: 0,
          });
        });
    }
  }, [continueCheck]);

  useEffect(() => {
    if (obligationFormResult && !obligationFormResult.isFinished) {
      setModalResultInfo(obligationFormResult.modalInfo);
      setContinueCheck(Math.random());
    } else if (obligationFormResult && obligationFormResult.isFinished) {
      setShowResultsModal(false);
      setShowAssignModal(true);
      setContinueCheck(Math.random());
    }
  }, [obligationFormResult]);

  const loadObligationFormAssign = useCallback(
    async () =>
      axios
        .post(
          axiosPath('api_r_user_m_legalCompliance_plantObligationFormAssign'),
          {
            moduleBridgeHex,
            plantHex,
            tempBatchLoopId: modalAssignInfo?.tempBatchLoopId,
          }
        )
        .then((r) => {
          if (isMounted.current) {
            setModalAssignInfo(r.data);
          }
        })
        .catch(() => {
          AppNotificationAlert({
            title: ReactDOMServer.renderToString(
              <Translator file="module-legalCompliance" name="error" />
            ),
            message: ReactDOMServer.renderToString(
              <Translator file="module-legalCompliance" name="error" />
            ),
            type: 'danger',
            position: 'top-center',
            duration: 0,
          });
        }),
    [moduleBridgeHex, plantHex, modalAssignInfo]
  );

  useEffect(() => {
    if (!modalAssignInfo?.isFinished) setContinueCheck(Math.random());
  }, [modalAssignInfo]);

  useEffect(() => {
    if (showAssignModal) loadObligationFormAssign();
  }, [continueCheck]);

  const handleDownloadFile = async (
    obligationFormTemplateId,
    questionCode,
    subquestionCode
  ) => {
    setIsDownloadingFile(true);
    await axios
      .post(
        axiosPath(
          'api_r_user_m_legalCompliance_plantObligationFormQuestionHelpFileDownload'
        ),
        {
          plantHex,
          obligationFormTemplateId,
          questionCode,
          subquestionCode,
        }
      )
      .then((r) => window.open(r.data, '_blank'))
      .catch(() => {
        AppNotificationAlert({
          title: ReactDOMServer.renderToString(
            <Translator file="module-legalCompliance" name="error" />
          ),
          message: ReactDOMServer.renderToString(
            <Translator file="module-legalCompliance" name="error" />
          ),
          type: 'danger',
          position: 'top-right',
          duration: 3000,
        });
      })
      .finally(() => {
        if (isMounted.current) setIsDownloadingFile(false);
      });
  };

  return (
    <section>
      <BreadcrumbHandler
        paths={
          moduleBridge && plantObligationForm
            ? [
                {
                  name: `${
                    moduleBridge.legalCompliance.plantName
                  } - ${ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="evaluation"
                    />
                  )}`,
                  route: {
                    name: 'user_module_legalCompliance_evaluation',
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="obligations.form"
                    />
                  ),
                },
              ]
            : [
                {
                  name: '...',
                },
              ]
        }
      />
      {plantObligationForm && (
        <>
          {draft && (
            <div className="alert alert-info mb-2">
              <Translator
                file="module-legalCompliance"
                name="form-saved-as-draft-on"
              />{' '}
              <b>{draft}</b>
            </div>
          )}
          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body p-2">
                  {plantObligationForm.questions.map(
                    (question, questionIndex) => (
                      <div className="row" key={questionIndex}>
                        <div className="col-lg-12">
                          {question.separator ? (
                            <div className="d-flex align-items-center wl-title wl-title-md wl-title-color font-weight-bold mb-1">
                              <div
                                className="d-inline-block w-100"
                                style={{
                                  height: '2px',
                                  backgroundColor: '#34495e',
                                }}
                              />
                            </div>
                          ) : (
                            <>
                              {question.type && (
                                <>
                                  {question.type !== 5 && (
                                    <>
                                      {question?.help && (
                                        <span className="float-right">
                                          <HelpModal
                                            size="xl"
                                            iconSize={16}
                                            iconColor="#28a745"
                                            iconFilled
                                          >
                                            {question.help.text && (
                                              <div
                                                dangerouslySetInnerHTML={{
                                                  __html: question.help.text,
                                                }}
                                              />
                                            )}
                                            {question.help.file && (
                                              <>
                                                <h6 className="mb-3">
                                                  <Translator
                                                    file="module-legalCompliance"
                                                    name="file"
                                                  />
                                                  :
                                                </h6>
                                                <FileName
                                                  title={ReactDOMServer.renderToString(
                                                    <Translator
                                                      file="module-legalCompliance"
                                                      name="download"
                                                    />
                                                  )}
                                                  onClick={() =>
                                                    isDownloadingFile
                                                      ? undefined
                                                      : handleDownloadFile(
                                                          question.obligationFormTemplateId,
                                                          question.parentCode ||
                                                            question.code,
                                                          question.parentCode
                                                            ? question.code
                                                            : undefined
                                                        )
                                                  }
                                                >
                                                  <AppRemixIcon
                                                    className="mr-3"
                                                    icon="download"
                                                  />
                                                  {question.help.file}
                                                </FileName>
                                              </>
                                            )}
                                          </HelpModal>
                                        </span>
                                      )}
                                      <span className="wl-title wl-title-md wl-title-color font-weight-bold">
                                        {question.name}
                                      </span>
                                      <br />
                                      <ObligationFormQuestionSelect
                                        question={question}
                                        onChange={(a) =>
                                          handleQuestionOnChange(question, a)
                                        }
                                        checkedQuestions={
                                          plantObligationForm?.result
                                            ?.userQuestions
                                        }
                                      />
                                    </>
                                  )}

                                  {((question.type === 4 &&
                                    selectedValues[question.code] &&
                                    selectedValues[question.code] ===
                                      question.answers[1].code) ||
                                    question.type === 5 ||
                                    (question.type === 6 &&
                                      selectedValues[question.code] &&
                                      selectedValues[question.code] ===
                                        question.answers[1].code)) && (
                                    <div
                                      className={
                                        question.type !== 5 ? 'ml-5' : ''
                                      }
                                    >
                                      {question.subquestions.map(
                                        (subQuestion, subQuestionIndex) => (
                                          <div
                                            className="row"
                                            key={subQuestionIndex}
                                          >
                                            <div className="col-lg-12">
                                              {subQuestion.separator ? (
                                                <div className="d-flex align-items-center wl-title wl-title-md wl-title-color font-weight-bold mb-1">
                                                  <div
                                                    className="d-inline-block w-100"
                                                    style={{
                                                      height: '2px',
                                                      backgroundColor:
                                                        '#34495e',
                                                    }}
                                                  />
                                                </div>
                                              ) : (
                                                <>
                                                  {subQuestion.type && (
                                                    <>
                                                      {subQuestion.help && (
                                                        <span className="float-right">
                                                          <HelpModal
                                                            size="xl"
                                                            iconSize={16}
                                                            iconColor="#28a745"
                                                            iconFilled
                                                          >
                                                            {subQuestion.help
                                                              .text && (
                                                              <div
                                                                dangerouslySetInnerHTML={{
                                                                  __html:
                                                                    subQuestion
                                                                      .help
                                                                      .text,
                                                                }}
                                                              />
                                                            )}
                                                            {subQuestion.help
                                                              .file && (
                                                              <>
                                                                <h6 className="mb-3">
                                                                  Archivo:
                                                                </h6>
                                                                <FileName
                                                                  title={ReactDOMServer.renderToString(
                                                                    <Translator
                                                                      file="module-legalCompliance"
                                                                      name="download"
                                                                    />
                                                                  )}
                                                                  onClick={() =>
                                                                    isDownloadingFile
                                                                      ? undefined
                                                                      : handleDownloadFile(
                                                                          question.obligationFormTemplateId,
                                                                          question.code,
                                                                          subQuestion.code
                                                                        )
                                                                  }
                                                                >
                                                                  <AppRemixIcon
                                                                    className="mr-3"
                                                                    icon="download"
                                                                  />
                                                                  {
                                                                    subQuestion
                                                                      .help.file
                                                                  }
                                                                </FileName>
                                                              </>
                                                            )}
                                                          </HelpModal>
                                                        </span>
                                                      )}
                                                      <span className="wl-title wl-title-md wl-title-color font-weight-bold">
                                                        {subQuestion.name}
                                                      </span>
                                                      <br />
                                                      <ObligationFormQuestionSelect
                                                        question={subQuestion}
                                                        onChange={(a) =>
                                                          handleQuestionOnChange(
                                                            subQuestion,
                                                            a
                                                          )
                                                        }
                                                        checkedQuestions={
                                                          plantObligationForm
                                                            ?.result
                                                            ?.userQuestions
                                                        }
                                                      />
                                                    </>
                                                  )}
                                                </>
                                              )}
                                            </div>
                                          </div>
                                        )
                                      )}
                                    </div>
                                  )}
                                </>
                              )}
                              {question.freeText && (
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: question.freeText,
                                  }}
                                />
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
          <FloatingButtons>
            {plantObligationForm?.recalculateAll ? (
              <>
                <AppModal
                  show={showConfirmModal}
                  isConfirmBtn
                  onConfirm={() => {
                    if (!isLoading) {
                      setShowConfirmModal(false);
                      handleOnFinish();
                    }
                  }}
                  confirmBtnText={<Translator file="user" name="user.accept" />}
                  isCancelBtn
                  cancelBtnText={<Translator file="user" name="user.cancel" />}
                  onClose={() => setShowConfirmModal(false)}
                  title={ReactDOMServer.renderToString(
                    <Translator file="module-legalCompliance" name="confirm" />
                  )}
                >
                  <Translator
                    file="module-legalCompliance"
                    name="obligation.form.confirm"
                  />
                </AppModal>
                <span
                  className={`btn btn-success float-right mt-1 d-inline-flex align-items-center ${
                    isLoading ? 'disabled' : ''
                  }`}
                  onClick={() => !isLoading && setShowConfirmModal(true)}
                >
                  <AppRemixIcon icon="save" className="mr-1" />
                  <Translator file="module-legalCompliance" name="finish" />
                </span>
              </>
            ) : (
              <span
                className={`btn btn-success float-right mt-1 d-inline-flex align-items-center ${
                  isLoading ? 'disabled' : ''
                }`}
                onClick={() => !isLoading && handleOnFinish()}
              >
                <AppRemixIcon icon="save" className="mr-1" />
                <Translator file="module-legalCompliance" name="finish" />
              </span>
            )}

            <span
              className={`btn btn-info float-right mt-1 mr-2 d-inline-flex align-items-center ${
                isLoading ? 'disabled' : ''
              }`}
              onClick={() => !isLoading && handleSaveDraft()}
            >
              <AppRemixIcon icon="draft" className="mr-1" />
              <Translator file="module-legalCompliance" name="save.as.draft" />
            </span>
          </FloatingButtons>
        </>
      )}
      <AppModal
        show={showIsFinishedModal}
        type="info"
        isConfirmBtn
        onConfirm={() =>
          navigate(
            path({
              name: 'user_module_legalCompliance_evaluation',
              params: {
                moduleBridgeHex,
                plantHex,
                page: 1,
                filtersHex: 0,
              },
            })
          )
        }
        title={ReactDOMServer.renderToString(
          <Translator file="module-legalCompliance" name="form.finished" />
        )}
      >
        <Translator
          file="module-legalCompliance"
          name="form.finished.worldlex.contact.24"
        />
      </AppModal>
      <AppModal
        size="lg"
        show={showResultsModal}
        title={ReactDOMServer.renderToString(
          <Translator file="module-intelligence" name="loading.results" />
        )}
      >
        <h2>{modalResultInfo.message}</h2>
        <ProgressBar animated now={modalResultInfo.percent} />
      </AppModal>
      <AppModal size="lg" show={showAssignModal}>
        <h2>{modalAssignInfo?.modalInfo?.message}</h2>
        {modalAssignInfo?.isFinished ? (
          <span
            className="btn btn-primary btn-sm mt-3 float-right"
            onClick={() =>
              (window.location.href = `${global.urlData.rootProject}r/legal-compliance/${moduleBridgeHex}/evaluation/${plantHex}/1/0/`)
            }
          >
            <Translator file="user" name="user.accept" />
          </span>
        ) : (
          <ProgressBar animated now={modalAssignInfo?.modalInfo?.percent} />
        )}
      </AppModal>
    </section>
  );
};

export default LegalCompliancePlantObligationFormMakeScreen;

const FileName = styled.div`
  cursor: pointer;
`;

const FloatingButtons = styled.div`
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  height: 45px;
  padding-right: 15px;
  background: ${colors.bg};
  border-top: 1px solid ${colors.grey300};
`;
